// src/components/ui/MediaPreview.jsx

import React, { forwardRef, useEffect } from "react";

/**
 * ┌────────────────────────────────────────────────────────────┐
 * │                      MediaPreview                          │
 * └────────────────────────────────────────────────────────────┘
 * Reusable visual renderer for thumbnails and media previews.
 *
 * Contract:
 * - file.file_url  : string (authoritative URL)
 * - file.file_type : "image" | "video" (NOT MIME type)
 *
 * ✅ Each unique file_url gets a unique key to prevent DOM reuse
 * ✅ Cleanup on unmount to prevent memory leaks
 * ✅ Grid thumbnails use preload="none" - no video bytes are fetched until the
 *    submission is opened. Seeking to a frame (currentTime = 0.1) forces the
 *    browser to download and decode real video data despite preload="metadata",
 *    which cost roughly a dozen range requests per file per page load. Cards
 *    show the play-button overlay instead of a first-frame preview; a real
 *    poster image needs generated derivatives, which do not exist yet.
 * ✅ Opened videos still preload metadata and seek to the first frame.
 */
const MediaPreview = forwardRef(({ file, thumbnail = false }, videoRef) => {
  if (!file || !file.file_url || !file.file_type) return null;

  const isVideo = file.file_type === "video";

  // Derivatives are empty strings until the media worker finishes, so an
  // unprocessed or failed file transparently serves the original.
  const imageSrc =
    (thumbnail ? file.thumb_url : file.preview_url) || file.file_url;


  // ✅ Cleanup on unmount
  useEffect(() => {
    return () => {
      if (videoRef?.current && isVideo && !thumbnail) {
        videoRef.current.pause();
        videoRef.current.removeAttribute("src");
        videoRef.current.load();
      }
    };
  }, [videoRef, isVideo, thumbnail]);

  return (
    <div className={`w-full h-full ${thumbnail ? "bg-gray-900" : ""}`}>
      {isVideo ? (
        thumbnail ? (
          <div className="relative w-full h-full bg-black">
            {file.poster_url ? (
              // A generated still - no video bytes are fetched at all.
              <img
                key={`poster-${file.file_url}`}
                src={file.poster_url}
                alt="video preview"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover opacity-90"
              />
            ) : (
              <video
                key={`thumb-${file.file_url}`}
                src={file.file_url}
                className="w-full h-full object-cover opacity-90"
                muted
                playsInline
                preload="none"
              />
            )}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="bg-black/60 rounded-full p-2 shadow-md">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="white"
                  viewBox="0 0 24 24"
                  className="w-5 h-5"
                >
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
            </div>
          </div>
        ) : (
          <video
            ref={videoRef}
            key={`video-${file.file_url}`}
            src={file.file_url}
            poster={file.poster_url || undefined}
            controls
            playsInline
            preload="metadata"
            controlsList="nodownload noremoteplayback"
            disablePictureInPicture
            className="w-full h-full object-contain swiper-no-swiping"
            onLoadedMetadata={(e) => {
              // Seek to first frame so thumbnail preview is visible
              e.currentTarget.currentTime = 0.1;
            }}
          >
            Your browser does not support the video tag.
          </video>
        )
      ) : (
        <img
          key={`img-${file.file_url}`}
          src={imageSrc}
          alt="submission preview"
          loading={thumbnail ? "lazy" : "eager"}
          decoding="async"
          className={
            thumbnail
              ? "w-full h-full object-cover"
              : "w-full h-full object-contain"
          }
          onError={(e) => {
            // A derivative can 404 if it was deleted or never finished. Fall
            // back to the original once; guard against a loop when the
            // original is itself missing.
            if (e.currentTarget.src !== file.file_url) {
              e.currentTarget.src = file.file_url;
            }
          }}
        />
      )}
    </div>
  );
});

MediaPreview.displayName = "MediaPreview";

export default MediaPreview;